/**
 * Staff CERS Facility Matching grid.
 *
 * Lists scraped CERS facilities alongside their Encamp match status and any
 * EPA-id / CERS-id suggestions, and surfaces unmatched/suggested/matched stat
 * cards that filter the grid. Gated behind the `cers-tracker` feature flag.
 *
 * Built on OmnisearchDataGridV2 — see hooks/omnisearch-v2/README.md and
 * routes/Customer/Chemicals/Facilities/FacilitiesTableV2.tsx for the pattern.
 */

import { useMutation, useQuery } from "@apollo/client";
import { alpha, Box, Button, Skeleton, Stack, Typography, useTheme } from "@mui/material";
import {
  GridColumnVisibilityModel,
  GridRowId,
  GridRowParams,
  GridSortModel,
} from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { FilterGroup } from "components/Omnisearch/FilterPopover";
import { StatusChip, StatusChipColor } from "components/StatusChip";
import {
  CersFacilityMatchesQuery,
  CersFacilityMatchesQueryVariables,
  CersFacilityMatchFilter,
  CersFacilityMatchStatus,
} from "generated-graphql/graphql";
import {
  extractOmnisearchValue,
  OmnisearchDataGridV2,
  useFullOmnisearch,
  useOmnisearchActions,
} from "hooks/omnisearch-v2";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid/useOmnisearchDatagrid";
import { useCallback, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { CERS_FACILITY_MATCHES, CERS_FACILITY_MATCH_STATS, LINK_CERS_FACILITY } from "./api";
import { CersFacilityMatchDialog } from "./CersFacilityMatchDialog";
import { CandidateFacilityCell, CersFacilityCell, LinkedIndicator } from "./MatchCells";
import { confidenceDisplay, matchBasisLabel, matchedFields, matchRowMode, Row } from "./matchRow";

// ════════════════════════════════════════════════════════════════════════════
// MODULE LEVEL - Static config (stable references, zero runtime overhead)
// ════════════════════════════════════════════════════════════════════════════

const PAGE_SIZE_OPTIONS = [25, 50, 100];

// Default order Unmatched → Suggested → Matched. The Postgres enum is declared
// MATCHED_CERS, SUGGESTED, UNMATCHED, so `matchStatus DESC` yields exactly that.
const INITIAL_SORT_MODEL: GridSortModel = [{ field: "matchStatus", sort: "desc" }];

// Granular columns kept for omnisearch filtering but hidden — the rich CERS /
// Encamp cells carry the visible data (Ben's "manage columns" note).
const COLUMN_VISIBILITY: GridColumnVisibilityModel = {
  epaId: false,
  matchStatus: false,
};

const getItems = (data: CersFacilityMatchesQuery) => data.cersFacilityMatches.items;
const getCount = (data: CersFacilityMatchesQuery) => data.cersFacilityMatches.count;
const getRowId = (row: Row): GridRowId => row.cersId;

/** Returns "auto" row height so multi-line rich cells expand naturally. */
const getRowHeight = () => "auto" as const;

/** Maps a CERS match status to its design-system StatusChip color + label. */
const MATCH_STATUS_DISPLAY: Record<
  CersFacilityMatchStatus,
  { color: StatusChipColor; label: string }
> = {
  [CersFacilityMatchStatus.MatchedCers]: { color: "success", label: "Matched" },
  [CersFacilityMatchStatus.Suggested]: { color: "info", label: "Suggested" },
  [CersFacilityMatchStatus.Unmatched]: { color: "warning", label: "Unmatched" },
};

/** Set used to validate omnisearch token values before treating them as the enum. */
const MATCH_STATUS_VALUES = new Set<string>(Object.values(CersFacilityMatchStatus));

/** Type guard: narrows an arbitrary string to CersFacilityMatchStatus without an as-cast. */
const isCersFacilityMatchStatus = (value: string): value is CersFacilityMatchStatus =>
  MATCH_STATUS_VALUES.has(value);

/**
 * Filter popover groups. Fields correspond to omnisearch key tokens that
 * buildVariables reads via extractOmnisearchValue and maps to
 * CersFacilityMatchFilter fields.
 */
const FILTER_GROUPS: FilterGroup[] = [{ name: "Match Status", fields: ["matchStatus"] }];

// OmnisearchDataGridV2 wraps its content in Box with px:3. Override sx to
// strip the horizontal padding so the grid left-aligns with the stat cards.
const GRID_SX = {
  px: 0,
  pt: 1,
  "& .MuiDataGrid-row": { cursor: "pointer" },
  // Top-align the CERS + Encamp rich cells so their lines stay horizontally
  // aligned across the two columns for easy comparison (team feedback).
  "& .cers-rich-cell": { alignItems: "flex-start" },
};

// ════════════════════════════════════════════════════════════════════════════
// SUB-COMPONENTS (module-level for stable references)
// ════════════════════════════════════════════════════════════════════════════

/** Encamp matched facility rich cell: name + link. */
function MatchedFacilityCell({ row }: { row: Row }) {
  if (!row.encampFacility)
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  if (!row.encampFacility.tenantId) {
    return <Typography variant="body2">{row.encampFacility.name}</Typography>;
  }
  return (
    <Link
      to={`/o/${row.encampFacility.tenantId}/facilities/${row.encampFacility.id}/profile`}
      target="_blank"
      style={{ color: "inherit", textDecoration: "none" }}
      onClick={(e) => e.stopPropagation()}
    >
      <Typography variant="body2" fontWeight="medium" sx={{ py: 0.75 }}>
        {row.encampFacility.name}
      </Typography>
    </Link>
  );
}

/**
 * Clickable stat card acting as a status filter. Label + status dot on top,
 * count below; the active filter gets a tinted background + colored border in
 * its own status color so the selected state is obvious (per UX designs).
 */
function StatusMetricCard({
  name,
  value,
  loading,
  color,
  selected,
  onClick,
}: {
  name: string;
  value: number | undefined;
  loading: boolean;
  color: "success" | "info" | "warning";
  selected: boolean;
  onClick: () => void;
}) {
  const theme = useTheme();
  const accent = theme.palette[color].main;
  return (
    <Box
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onClick();
        }
      }}
      sx={{
        minWidth: 150,
        cursor: "pointer",
        px: 2,
        py: 1.25,
        borderRadius: 1.5,
        border: `1px solid ${selected ? accent : theme.palette.divider}`,
        bgcolor: selected ? alpha(accent, 0.1) : theme.palette.background.paper,
        transition: "all 0.15s ease-in-out",
        "&:hover": { borderColor: accent, bgcolor: alpha(accent, selected ? 0.14 : 0.04) },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.75}>
        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: accent, flexShrink: 0 }} />
        <Typography variant="caption" color="text.secondary" noWrap>
          {name}
        </Typography>
      </Stack>
      <Typography variant="h5" fontWeight={700} lineHeight={1.2} sx={{ mt: 0.25 }}>
        {loading ? <Skeleton variant="text" width={40} /> : (value ?? 0)}
      </Typography>
    </Box>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// COMPONENT - Only dynamic stuff here
// ════════════════════════════════════════════════════════════════════════════

export const CersFacilities = () => {
  const alerts = useAlerts();

  const breadcrumbs = useMemo(() => [{ label: "Facilities", to: "/staff/cers/facilities" }], []);
  useBreadcrumb(breadcrumbs);

  // Stat cards, the popover, the search box, and the URL all share ONE source of
  // truth for status filtering: the omnisearch `matchStatus` token. Cards write it
  // via useOmnisearchActions so the search box + URL stay in sync with the grid.
  const currentOmnisearch = useFullOmnisearch();
  const { editKeyValues, removeKey } = useOmnisearchActions();
  const activeStatusToken = extractOmnisearchValue(currentOmnisearch, "matchStatus");
  const activeStatus: CersFacilityMatchStatus | null =
    activeStatusToken && isCersFacilityMatchStatus(activeStatusToken) ? activeStatusToken : null;

  // Match dialog state: which row was clicked (whole row opens the dialog).
  const [matchDialogRow, setMatchDialogRow] = useState<Row | null>(null);

  // Rows confirmed this session, mapped cersId → the facility we just linked it to.
  // We DON'T refetch the list on confirm (that would re-sort the row out of view
  // mid-task); instead we mark it linked IN PLACE and only the stat counts refresh.
  // The stored candidate lets a click on a just-confirmed row open the dialog in
  // its MATCHED state (the row object itself is stale SUGGESTED until a real
  // refetch), so re-picking still hits the override guard. (Team feedback: instant
  // feedback, no jump.)
  const [confirmedIds, setConfirmedIds] = useState<Map<string, { id: string; name: string }>>(
    () => new Map()
  );

  const { data: statsData, loading: statsLoading } = useQuery(CERS_FACILITY_MATCH_STATS);
  const stats = statsData?.cersFacilityMatchStats;

  const [linkCersFacility] = useMutation(LINK_CERS_FACILITY, {
    // Stats only — NOT the list, so the confirmed row stays put until a real refresh.
    refetchQueries: [CERS_FACILITY_MATCH_STATS],
  });

  const handleRowClick = useCallback(
    (params: GridRowParams<Row>) => {
      // If the row was confirmed this session, its cached object is still stale
      // (SUGGESTED). Present it to the dialog as MATCHED so re-picking routes
      // through the override guard.
      const confirmed = confirmedIds.get(params.row.cersId);
      if (confirmed) {
        setMatchDialogRow({
          ...params.row,
          matchStatus: CersFacilityMatchStatus.MatchedCers,
          encampFacility: { id: confirmed.id, name: confirmed.name, tenantId: null },
          suggestions: [],
        });
        return;
      }
      setMatchDialogRow(params.row);
    },
    [confirmedIds]
  );

  const handleConfirm = useCallback(
    (row: Row) => {
      const { suggestion } = matchRowMode(row);
      if (!suggestion) return;
      const candidateName = suggestion.candidateFacility.name ?? suggestion.candidateFacility.id;
      void linkCersFacility({
        variables: {
          cersFacilityId: row.cersId,
          encampFacilityId: suggestion.candidateFacility.id,
        },
      })
        .then(() => {
          setConfirmedIds((prev) =>
            new Map(prev).set(row.cersId, {
              id: suggestion.candidateFacility.id,
              name: candidateName,
            })
          );
          alerts.success(`Linked CERS facility "${row.name ?? row.cersId}" to "${candidateName}"`);
        })
        .catch((err: unknown) => {
          alerts.error(`Failed to link: ${err instanceof Error ? err.message : String(err)}`, err);
        });
    },
    [alerts, linkCersFacility]
  );

  // ──────────────────────────────────────────────────────────────────────────
  // useMemo - Arrays with callbacks (columns have valueGetter, renderCell, etc.)
  // ──────────────────────────────────────────────────────────────────────────
  const columns: OmnisearchGridColDef<Row>[] = useMemo(
    () => [
      // ── Visible rich columns ──────────────────────────────────────────────

      {
        field: "match",
        headerName: "Match",
        width: 140,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          // Just-confirmed this session → show Matched in place (no re-sort).
          if (confirmedIds.has(row.cersId)) {
            const display = MATCH_STATUS_DISPLAY[CersFacilityMatchStatus.MatchedCers];
            return <StatusChip color={display.color} label={display.label} />;
          }
          const { mode, suggestion } = matchRowMode(row);
          if (mode === "matched") {
            const display = MATCH_STATUS_DISPLAY[CersFacilityMatchStatus.MatchedCers];
            return <StatusChip color={display.color} label={display.label} />;
          }
          if (mode === "find") {
            const display = MATCH_STATUS_DISPLAY[CersFacilityMatchStatus.Unmatched];
            return <StatusChip color={display.color} label={display.label} />;
          }
          // Single suggestion (any tier, incl. Low) → tier + why it earned it.
          if (suggestion) {
            const { color, label } = confidenceDisplay(suggestion.confidence);
            return (
              <Stack spacing={0.25} alignItems="flex-start" sx={{ py: 0.75 }}>
                <StatusChip color={color} label={label} />
                <Typography variant="caption" color="text.secondary">
                  {matchBasisLabel(suggestion.source)}
                </Typography>
              </Stack>
            );
          }
          // Multiple candidates → ambiguous, resolve in the dialog.
          const count = row.suggestions?.length ?? 0;
          return <StatusChip color="info" label={`${count} matches`} />;
        },
      },

      {
        field: "cersFacility",
        headerName: "CERS Facility",
        cellClassName: "cers-rich-cell",
        flex: 0.4,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          const { suggestion } = matchRowMode(row);
          const bold = suggestion ? matchedFields(row, suggestion.candidateFacility) : undefined;
          return <CersFacilityCell row={row} bold={bold} />;
        },
      },

      {
        field: "suggestedEncamp",
        headerName: "Suggested Encamp Facility",
        cellClassName: "cers-rich-cell",
        flex: 0.4,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          const { mode, suggestion } = matchRowMode(row);
          if (mode === "matched") {
            return <MatchedFacilityCell row={row} />;
          }
          if (suggestion) {
            const bold = matchedFields(row, suggestion.candidateFacility);
            return <CandidateFacilityCell candidate={suggestion.candidateFacility} bold={bold} />;
          }
          return (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          );
        },
      },

      {
        // The whole row is clickable → opens the match dialog (find / review /
        // change). The ONLY inline action is Confirm (a low-emphasis text button
        // that stops propagation so it doesn't also open the dialog).
        field: "actions",
        headerName: "",
        width: 150,
        sortable: false,
        filterable: false,
        renderCell: ({ row }) => {
          if (confirmedIds.has(row.cersId)) {
            return <LinkedIndicator />;
          }
          const { mode } = matchRowMode(row);
          if (mode === "matched") {
            return <LinkedIndicator />;
          }
          if (mode === "confirm") {
            return (
              <Button
                size="small"
                variant="text"
                onClick={(e) => {
                  e.stopPropagation();
                  handleConfirm(row);
                }}
              >
                Confirm match
              </Button>
            );
          }
          // review / find → no button; clicking the row opens the dialog.
          return null;
        },
      },

      // ── Hidden granular columns — kept for omnisearch sort ────────────────

      {
        field: "epaId",
        headerName: "CERS EPA ID",
        flex: 0.25,
        filterable: false,
        valueGetter: (_, row) => row.epaId ?? "",
      },
      {
        field: "matchStatus",
        headerName: "Match Status",
        flex: 0.25,
        valueGetter: (_, row) => row.matchStatus ?? "",
      },
    ],
    [handleConfirm, confirmedIds]
  );

  // ──────────────────────────────────────────────────────────────────────────
  // useCallback - Build query variables purely from the omnisearch string.
  // Stat cards, the popover, and the search box all write into that one string,
  // so the matchStatus token and free-text search are read here and combined into
  // one CersFacilityMatchFilter — a single source of truth (grid ↔ search box ↔ URL).
  // ──────────────────────────────────────────────────────────────────────────
  const buildVariables = useCallback(
    (
      omnisearch: string,
      pagination: { page: number; pageSize: number },
      sort: GridSortModel
    ): CersFacilityMatchesQueryVariables => {
      // matchStatus comes solely from the omnisearch token — stat cards and the
      // popover both write it — so grid, search box, and URL stay consistent.
      // Validated against the enum; unknown strings are dropped.
      const matchStatusToken = extractOmnisearchValue(omnisearch, "matchStatus");
      const resolvedMatchStatus: CersFacilityMatchStatus | undefined =
        matchStatusToken && isCersFacilityMatchStatus(matchStatusToken)
          ? matchStatusToken
          : undefined;

      // Whatever the user types free-form (no `key:value` token) is the search
      // term → server-side name / EPA id / address search. Strip ONLY the known
      // matchStatus token so free text with a colon (e.g. a ZIP like "CA:90210")
      // survives as the search term rather than being eaten.
      const searchText = omnisearch.replace(/(?:^|\s)matchStatus:(?:"[^"]+"|\S+)/g, "").trim();
      const search = searchText.length > 0 ? searchText : undefined;

      const hasAnyFilter = resolvedMatchStatus || search;
      const filter: CersFacilityMatchFilter | undefined = hasAnyFilter
        ? {
            ...(resolvedMatchStatus ? { matchStatus: resolvedMatchStatus } : {}),
            ...(search ? { search } : {}),
          }
        : undefined;

      return {
        filter,
        page: pagination.page,
        pageSize: pagination.pageSize,
        sort: sort.map((s) => ({ field: s.field, sort: s.sort })),
      };
    },
    []
  );

  // Clicking a card sets the shared matchStatus token; clicking the active card
  // again clears it — keeping the grid, search box, and URL consistent.
  const toggleStatusFilter = useCallback(
    (status: CersFacilityMatchStatus) => {
      if (activeStatus === status) {
        removeKey("matchStatus");
      } else {
        editKeyValues([["matchStatus", status]]);
      }
    },
    [activeStatus, editKeyValues, removeKey]
  );

  return (
    <Stack spacing={2} sx={{ p: 2 }}>
      <Typography variant="h5">CERS Facility Matching</Typography>
      <Stack direction="row" spacing={2}>
        <StatusMetricCard
          name="Unmatched"
          value={stats?.unmatched}
          loading={statsLoading}
          color="warning"
          selected={activeStatus === CersFacilityMatchStatus.Unmatched}
          onClick={() => toggleStatusFilter(CersFacilityMatchStatus.Unmatched)}
        />
        <StatusMetricCard
          name="Suggested"
          value={stats?.suggested}
          loading={statsLoading}
          color="info"
          selected={activeStatus === CersFacilityMatchStatus.Suggested}
          onClick={() => toggleStatusFilter(CersFacilityMatchStatus.Suggested)}
        />
        <StatusMetricCard
          name="Matched"
          value={stats?.matched}
          loading={statsLoading}
          color="success"
          selected={activeStatus === CersFacilityMatchStatus.MatchedCers}
          onClick={() => toggleStatusFilter(CersFacilityMatchStatus.MatchedCers)}
        />
      </Stack>
      <OmnisearchDataGridV2
        query={CERS_FACILITY_MATCHES}
        getItems={getItems}
        getCount={getCount}
        getRowId={getRowId}
        persistenceKey="staff-cers-facilities-table-v2"
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        initialSortModel={INITIAL_SORT_MODEL}
        initialColumnVisibilityModel={COLUMN_VISIBILITY}
        filterGroups={FILTER_GROUPS}
        columns={columns}
        buildVariables={buildVariables}
        getRowHeight={getRowHeight}
        onRowClick={handleRowClick}
        noRowsMessage="No CERS facilities have been ingested yet."
        sx={GRID_SX}
      />
      <CersFacilityMatchDialog
        open={matchDialogRow !== null}
        cersFacility={matchDialogRow}
        onClose={() => setMatchDialogRow(null)}
      />
    </Stack>
  );
};
